import { css } from 'lit';
import { hostReset, focusRing } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  focusRing('.uwc-sb__btn', '--uwc-color-primary', '#6366f1'),
  css`
    :host {
      display: inline-block;
    }

    :host([fluid]) {
      display: block;
      width: 100%;
    }

    /* ── Group container ───────────────────────────────────────────────────── */
    .uwc-sb {
      display: inline-flex;
      align-items: stretch;
      min-height: var(--uwc-sb-height, ${t.controlHeightMd});
      border: 1px solid var(--uwc-sb-border, ${t.borderControl});
      border-radius: var(--uwc-sb-radius, ${t.radiusMd});
      background: var(--uwc-sb-bg, ${t.surface});
      overflow: hidden;
    }

    :host([fluid]) .uwc-sb {
      display: flex;
      width: 100%;
    }

    .uwc-sb--invalid {
      border-color: ${t.danger};
    }

    /* ── Individual button ─────────────────────────────────────────────────── */
    .uwc-sb__btn {
      flex: 1 1 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.375rem;
      padding:
        var(--uwc-sb-padding-y, 0)
        var(--uwc-sb-padding-x, 0.875rem);
      font-size:   var(--uwc-sb-font-size, ${t.fontSizeMd});
      font-weight: ${t.fontWeightMedium};
      line-height: 1.25;
      color:       var(--uwc-sb-color, ${t.text});
      background:  transparent;
      border:      none;
      border-right: 1px solid var(--uwc-sb-border, ${t.borderControl});
      cursor:      pointer;
      white-space: nowrap;
      user-select: none;
      transition:
        background   ${t.durationBase},
        color        ${t.durationBase},
        border-color ${t.durationBase};
      outline: none;
    }

    .uwc-sb__btn:last-child {
      border-right: none;
    }

    /* Hover — only on enabled, unselected buttons */
    .uwc-sb__btn:hover:not(.uwc-sb__btn--disabled):not(.uwc-sb__btn--selected) {
      background: var(--uwc-sb-hover-bg, ${t.hoverBg});
    }

    /* ── Selected state ────────────────────────────────────────────────────── */
    .uwc-sb__btn--selected {
      background:  var(--uwc-sb-selected-bg, var(--uwc-color-primary, ${t.primary}));
      color:       var(--uwc-sb-selected-color, ${t.colorWhite});
      /* Divider to the right of a selected button takes the accent colour */
      border-right-color: var(--uwc-sb-selected-border, var(--uwc-color-primary, ${t.primary}));
    }

    /* Divider to the left of a button that follows a selected one */
    .uwc-sb__btn--selected + .uwc-sb__btn {
      border-left: 1px solid var(--uwc-sb-selected-border, var(--uwc-color-primary, ${t.primary}));
    }

    /* ── Disabled option ───────────────────────────────────────────────────── */
    .uwc-sb__btn--disabled {
      opacity:        0.45;
      cursor:         not-allowed;
      pointer-events: none;
    }

    /* ── Disabled host ─────────────────────────────────────────────────────── */
    :host([disabled]) .uwc-sb {
      opacity:        0.5;
      pointer-events: none;
    }

    /* ── Size variants ───────────────────────────────────────────────────────
       Component-level sizes fall back to intrinsic (padding-driven) height so
       they stay compact/roomy independent of the shared control height. */
    :host([size="small"]) .uwc-sb { min-height: ${t.controlHeightSm}; }
    :host([size="small"]) .uwc-sb__btn {
      padding:   0 0.625rem;
      font-size: ${t.fontSizeSm};
    }

    :host([size="large"]) .uwc-sb { min-height: ${t.controlHeightLg}; }
    :host([size="large"]) .uwc-sb__btn {
      padding:   0 1.125rem;
      font-size: ${t.fontSizeLg};
    }

    /* ── Icon ──────────────────────────────────────────────────────────────── */
    .uwc-sb__icon {
      flex-shrink: 0;
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-sb__btn { transition: none; }
    }
  `,
];
